import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import type { PrefetchContext, PrefetchedDataProps, PrefetchedUserDataProps } from 'src/shared/prefetch';

import { isSunriseDashboardPageAccessExperimentEnabled } from 'src/app/pages/starterDashboard/shared/utils';
import { useCountryCode, useLocale } from 'src/app/shared/hooks/l10n';
import type { MergedState } from 'src/app/shared/hooks/state';
import { useMergedStates } from 'src/app/shared/hooks/state';

import { prefetchStarterDashboardPageDataFromApi, useStarterDashboardPageDataFromApi } from './pageData';
import { prefetchStarterDashboardPageFcm, useAsyncStarterDashboardPageFcm } from './pageFcm';
import {
	prefetchStarterDashboardPageFcmExperiments,
	useAsyncStarterDashboardPageFcmExperiments,
} from './pageFcmExperiments';

export type StarterDashboardPagePrefetchedContext = Readonly<{
	pageData: Parameters<typeof useStarterDashboardPageDataFromApi>[0];
}>;

export type StarterDashboardPageCombinedState = MergedState<
	ReturnType<typeof useStarterDashboardPageDataFromApi>,
	ReturnType<typeof useAsyncStarterDashboardPageFcmExperiments>,
	ReturnType<typeof useAsyncStarterDashboardPageFcm>
>;

export function usePageContext(
	slug: string,
	prefetchedData?: StarterDashboardPagePrefetchedContext,
): StarterDashboardPageCombinedState {
	const locale = useLocale();
	const countryCode = useCountryCode();
	return useMergedStates(
		useStarterDashboardPageDataFromApi(prefetchedData?.pageData, { slug, countryCode, locale }),
		useAsyncStarterDashboardPageFcmExperiments(),
		useAsyncStarterDashboardPageFcm(),
	);
}

export async function prefetchPageContext(context: PrefetchContext): Promise<StarterDashboardPagePrefetchedContext> {
	const [pageData] = await Promise.all([
		prefetchStarterDashboardPageDataFromApi(context, {
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			slug: context.params.slug!,
			countryCode: context.l10n.countryCode,
			locale: context.l10n.locale,
		}),
		prefetchStarterDashboardPageFcmExperiments(context),
		prefetchStarterDashboardPageFcm(context),
	]);

	return { pageData };
}

type StarterDashboardPrefetchedUserContext = { sunriseDashboardPageAccessEnabled: boolean };

export async function prefetchUserInfo(utilityContext: UtilityContext): Promise<StarterDashboardPrefetchedUserContext> {
	const sunriseDashboardPageAccessEnabled = await isSunriseDashboardPageAccessExperimentEnabled(utilityContext);
	return { sunriseDashboardPageAccessEnabled };
}

export type StarterDashboardPagePrefetchedDataProps = PrefetchedDataProps<StarterDashboardPagePrefetchedContext> &
	PrefetchedUserDataProps<StarterDashboardPrefetchedUserContext>;
